/* workspace/notes — NotesView (production-quality CRM module).
   Globals via _bridge: React, useStore, useToast, ModuleHeader, Btn, IconBtn,
   NexusIcon, Avatar, FilterChip, useFilters, useTableState, SortableTh */
const { useState: useStateN, useEffect: useEffectN, useMemo: useMemoN,
        useRef: useRefN, useCallback: useCallbackN } = React;

/* ── localStorage pin persistence ─────────────────────────────── */
const _pinKey   = (wsId) => `nx-pins-notes-${wsId || 'default'}`;
const _loadPins = (wsId) => { try { return new Set(JSON.parse(localStorage.getItem(_pinKey(wsId)) || '[]')); } catch (_) { return new Set(); } };
const _savePins = (wsId, s) => { try { localStorage.setItem(_pinKey(wsId), JSON.stringify([...s])); } catch (_) {} };

/* ── Tone hash ─────────────────────────────────────────────────── */
const NOTE_TONES = [
  'var(--nx-tone-primary)',
  'var(--nx-tone-rose)',
  'var(--nx-tone-success)',
  'var(--nx-tone-amber)',
  'var(--nx-tone-purple)',
];
const noteTone = (who) => {
  let h = 0;
  for (const ch of (who || '')) h = (h + (ch.codePointAt(0) || 0)) % NOTE_TONES.length;
  return NOTE_TONES[h];
};

/* ── Date helpers ──────────────────────────────────────────────── */
const _fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const _fmtTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ── module-level: only one NoteLinker dropdown open at a time ── */
let _nxLinkerClose = null;

/* ── NoteLinker — searchable floating popover link picker ────────── */
const NoteLinker = ({ icon, label, value, onChange, items, keyField = 'id', nameField = 'name' }) => {
  const [open,   setOpen]   = useStateN(false);
  const [query,  setQuery]  = useStateN('');
  const [style,  setStyle]  = useStateN({});
  const [cursor, setCursor] = useStateN(-1);
  const btnRef  = useRefN(null);
  const dropRef = useRefN(null);

  /* Stable close — useState setters are referentially stable across renders */
  const closeThis = useCallbackN(() => { setOpen(false); setQuery(''); setCursor(-1); }, []);
  const doClose   = useCallbackN(() => {
    closeThis();
    if (_nxLinkerClose === closeThis) _nxLinkerClose = null;
  }, [closeThis]);

  /* Calculate position:fixed coords from button's viewport rect */
  const computePos = useCallbackN(() => {
    if (!btnRef.current) return {};
    const r = btnRef.current.getBoundingClientRect();
    const flipUp = (window.innerHeight - r.bottom) < 252 && r.top > 252;
    return {
      position: 'fixed',
      left: r.left,
      width: Math.max(r.width, 200),
      zIndex: 9999,
      ...(flipUp
        ? { bottom: window.innerHeight - r.top + 4, top: 'auto' }
        : { top: r.bottom + 4, bottom: 'auto' }),
    };
  }, []);

  /* Toggle open/close */
  const toggle = useCallbackN(() => {
    if (open) { doClose(); return; }
    if (_nxLinkerClose && _nxLinkerClose !== closeThis) _nxLinkerClose();
    _nxLinkerClose = closeThis;
    setStyle(computePos());
    setOpen(true);
    setQuery('');
    setCursor(-1);
  }, [open, closeThis, doClose, computePos]);

  /* Outside-click, scroll/resize repositioning */
  useEffectN(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!btnRef.current?.contains(e.target) && !dropRef.current?.contains(e.target)) doClose();
    };
    const recompute = () => setStyle(computePos());
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open, doClose, computePos]);

  /* Clear global ref on unmount */
  useEffectN(() => () => { if (_nxLinkerClose === closeThis) _nxLinkerClose = null; }, [closeThis]);

  const filtered = useMemoN(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(it => (it[nameField] || '').toLowerCase().includes(q));
  }, [items, query, nameField]);

  /* Options: [clear?] then filtered items */
  const opts = useMemoN(() => {
    const list = value ? [{ _nx_clr: true }] : [];
    filtered.forEach(it => list.push(it));
    return list;
  }, [value, filtered]);

  const selected = value ? items.find(it => it[keyField] === value) : null;
  const display  = selected ? selected[nameField] : null;

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, opts.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); return; }
    if (e.key === 'Escape')    { doClose(); return; }
    if (e.key === 'Enter' && cursor >= 0 && cursor < opts.length) {
      e.preventDefault();
      const opt = opts[cursor];
      if (opt._nx_clr) { onChange(''); doClose(); }
      else { onChange(opt[keyField]); doClose(); }
    }
  };

  return (
    <div className="nx-note-linker">
      <button
        ref={btnRef}
        type="button"
        className={'nx-note-linker-btn' + (open ? ' is-open' : '')}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <NexusIcon name={icon} size={13} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
        {display
          ? <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
          : <span style={{ flex: 1, color: 'var(--nx-text-faint)' }}>{label}</span>
        }
        {display
          ? <button type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--nx-text-faint)', lineHeight: 1 }}
              aria-label={`Clear ${label}`}
              onClick={e => { e.stopPropagation(); onChange(''); }}>
              <NexusIcon name="X" size={11} />
            </button>
          : <NexusIcon name="ChevronDown" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
        }
      </button>

      {open && (
        <div ref={dropRef} className="nx-note-linker-drop" style={style} aria-label={label}>
          <div className="nx-note-linker-search">
            <NexusIcon name="Search" size={12} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
            <input
              autoFocus
              value={query}
              onChange={e => { setQuery(e.target.value); setCursor(-1); }}
              onKeyDown={onSearchKey}
              placeholder={`Search ${label.toLowerCase()}…`}
            />
          </div>
          <div className="nx-note-linker-list">
            {opts.length === 0
              ? <div className="nx-note-linker-empty">No {label.toLowerCase()} found</div>
              : opts.map((opt, idx) =>
                  opt._nx_clr
                    ? <button key="__clr" type="button"
                        className={'nx-note-linker-item is-clear' + (idx === cursor ? ' is-focused' : '')}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => { onChange(''); doClose(); }}>
                        <NexusIcon name="X" size={11} />
                        <span style={{ flex: 1 }}>Clear selection</span>
                      </button>
                    : <button key={opt[keyField]} type="button"
                        className={'nx-note-linker-item' + (opt[keyField] === value ? ' is-selected' : '') + (idx === cursor ? ' is-focused' : '')}
                        onMouseEnter={() => setCursor(idx)}
                        onClick={() => { onChange(opt[keyField]); doClose(); }}>
                        {opt[keyField] === value && <NexusIcon name="Check" size={11} style={{ flexShrink: 0 }} />}
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt[nameField]}</span>
                      </button>
                )
            }
          </div>
        </div>
      )}
    </div>
  );
};

/* ── NoteLinkedBadge ───────────────────────────────────────────── */
const NoteLinkedBadge = ({ note, people, companies, deals, size = 'sm' }) => {
  let icon = null, name = null;
  if (note.personId)  { const p = people.find(x => x.id === note.personId);   if (p) { icon = 'User';      name = p.name;  } }
  if (!name && note.companyId) { const c = companies.find(x => x.id === note.companyId); if (c) { icon = 'Building2'; name = c.name;  } }
  if (!name && note.dealId)    { const d = deals.find(x => x.id === note.dealId);    if (d) { icon = 'Target';    name = d.title; } }
  if (!name) return null;
  return (
    <span className={size === 'sm' ? 'nx-note-card-link' : 'nx-note-tbl-link'}>
      <NexusIcon name={icon} size={10} />
      <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    </span>
  );
};

/* ── NoteSkeleton ──────────────────────────────────────────────── */
const NoteSkel = () => (
  <div className="nx-note-skel">
    <div className="nx-note-skel-line" style={{ width: '55%', height: 12 }} />
    <div className="nx-note-skel-line" style={{ width: '80%' }} />
    <div className="nx-note-skel-line" style={{ width: '65%' }} />
    <div className="nx-note-skel-line" style={{ width: '35%', marginTop: 8 }} />
  </div>
);

/* ── NoteDetailPanel ───────────────────────────────────────────── */
const NoteDetailPanel = ({ note, pinned, people, companies, deals, onClose, onPin, onSave, onDelete }) => {
  const [editTitle, setEditTitle] = useStateN(note.noteTitle || '');
  const [editBody,  setEditBody]  = useStateN(note.detail    || '');
  const [editMode,  setEditMode]  = useStateN(false);
  const toast = useToast();

  useEffectN(() => {
    setEditTitle(note.noteTitle || '');
    setEditBody(note.detail    || '');
    setEditMode(false);
  }, [note.id]);

  useEffectN(() => {
    const handler = (e) => { if (e.key === 'Escape') { onClose(); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const save = () => {
    if (!editBody.trim()) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note body is required' }); return; }
    onSave(note.id, { body: editBody.trim(), title: editTitle.trim() || undefined });
    setEditMode(false);
  };

  const enterEdit = () => setEditMode(true);
  const tone  = noteTone(note.who);
  const isPin = pinned.has(note.id);

  return (
    <>
      <button type="button" className="nx-note-detail-veil" onClick={onClose} aria-label="Close note detail" />
      <div className="nx-note-detail-panel" aria-label="Note detail">
        {/* Header */}
        <div className="nx-note-detail-head">
          <span className="nx-note-card-tone" style={{ background: `color-mix(in srgb, ${tone} 9%, transparent)`, color: tone, width: 28, height: 28, flexShrink: 0 }}>
            <NexusIcon name="StickyNote" size={13} />
          </span>
          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)' }}>Note</span>
          <div className="nx-note-detail-actions">
            <button type="button" className={'nx-note-act' + (isPin ? ' is-active' : '')} title={isPin ? 'Unpin' : 'Pin'} onClick={() => onPin(note.id)}>
              <NexusIcon name="Pin" size={13} />
            </button>
            {!editMode && (
              <button type="button" className="nx-note-act" title="Edit note" onClick={enterEdit}>
                <NexusIcon name="Pencil" size={13} />
              </button>
            )}
            <button type="button" className="nx-note-act is-danger" title="Delete note" onClick={() => { onClose(); onDelete(note.id); }}>
              <NexusIcon name="Trash2" size={13} />
            </button>
            <button type="button" className="nx-note-detail-close" title="Close" onClick={onClose}>
              <NexusIcon name="X" size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="nx-note-detail-body">
          {editMode ? (
            <>
              <input
                className="nx-input"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                placeholder="Note title (optional)"
                style={{ fontWeight: 700, fontSize: 15 }}
                autoFocus
              />
              <textarea
                className="nx-input"
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                style={{ minHeight: 160, resize: 'vertical', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6 }}
                onKeyDown={e => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { save(); }
                  if (e.key === 'Escape') { setEditMode(false); }
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Btn variant="ghost" size="sm" onClick={() => setEditMode(false)}>Cancel</Btn>
                <Btn variant="primary" size="sm" icon="Check" onClick={save}>Save</Btn>
              </div>
            </>
          ) : (
            <>
              {/* Title — click to edit */}
              <button type="button"
                className={'nx-note-detail-title-view' + (!note.noteTitle ? ' is-untitled' : '')}
                onClick={enterEdit} title="Click to edit"
                style={{ background: 'none', border: 'none', cursor: 'text', textAlign: 'left', width: '100%', display: 'block' }}>
                {note.noteTitle || 'Untitled note — click to add title'}
              </button>

              {/* Body — click to edit */}
              <button type="button"
                className="nx-note-detail-body-view"
                onClick={enterEdit} title="Click to edit"
                style={{ background: 'none', border: 'none', cursor: 'text', textAlign: 'left', width: '100%', display: 'block' }}>
                {note.detail || <em style={{ color: 'var(--nx-text-faint)' }}>No content — click to add</em>}
              </button>
            </>
          )}

          {/* Metadata */}
          <div className="nx-note-detail-meta">
            <Avatar name={note.who} tone={tone} size={18} />
            <span style={{ fontWeight: 600 }}>{note.who}</span>
            {note.createdAt && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>Created {_fmtTime(note.createdAt)}</span>
              </>
            )}
            {note.updatedAt && note.updatedAt !== note.createdAt && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>Updated {_fmtTime(note.updatedAt)}</span>
              </>
            )}
            <NoteLinkedBadge note={note} people={people} companies={companies} deals={deals} size="md" />
          </div>
        </div>
      </div>
    </>
  );
};

/* ── NoteComposer ──────────────────────────────────────────────── */
const NoteComposer = ({ people, companies, deals, onSave, onClose }) => {
  const [title,     setTitle]     = useStateN('');
  const [body,      setBody]      = useStateN('');
  const [personId,  setPersonId]  = useStateN('');
  const [companyId, setCompanyId] = useStateN('');
  const [dealId,    setDealId]    = useStateN('');
  const toast = useToast();

  const save = () => {
    if (!body.trim()) { toast({ tone: 'warning', icon: 'AlertCircle', title: 'Note body is required' }); return; }
    onSave({ body: body.trim(), title: title.trim() || undefined, personId: personId || undefined, companyId: companyId || undefined, dealId: dealId || undefined });
    setTitle(''); setBody(''); setPersonId(''); setCompanyId(''); setDealId('');
  };

  return (
    <div className="nx-notes-composer-panel">
      <div className="nx-ncp-head">
        <NexusIcon name="StickyNote" size={14} style={{ color: 'var(--nx-accent)' }} />
        <span className="nx-ncp-title">New Note</span>
        <button type="button" className="nx-note-act" style={{ marginLeft: 'auto' }} onClick={onClose} title="Close composer">
          <NexusIcon name="X" size={12} />
        </button>
      </div>
      <div className="nx-ncp-body">
        <input
          className="nx-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Note title (optional)"
          style={{ fontWeight: 600 }}
          autoFocus
        />
        <textarea
          className="nx-input"
          style={{ minHeight: 100, resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Write a note… (Ctrl+Enter to save)"
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { save(); }
          }}
        />
        <div className="nx-ncp-links">
          <NoteLinker icon="User"      label="Link to Person"  value={personId}  onChange={setPersonId}  items={people}    nameField="name" />
          <NoteLinker icon="Building2" label="Link to Company" value={companyId} onChange={setCompanyId} items={companies} nameField="name" />
          <NoteLinker icon="Target"    label="Link to Deal"    value={dealId}    onChange={setDealId}    items={deals.filter(d => !d.archived)} nameField="title" />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--nx-text-faint)', flex: 1 }}>Ctrl+Enter to save</span>
          <Btn variant="ghost" onClick={() => { setTitle(''); setBody(''); setPersonId(''); setCompanyId(''); setDealId(''); }}>Clear</Btn>
          <Btn variant="primary" icon="Check" onClick={save}>Save Note</Btn>
        </div>
      </div>
    </div>
  );
};

/* ── NoteCard (Grid view) ──────────────────────────────────────── */
const NoteCard = ({ note, pinned, isSelected, people, companies, deals, onOpen, onPin, onDelete }) => {
  const tone  = noteTone(note.who);
  const isPin = pinned.has(note.id);

  return (
    <div className={'nx-note-card' + (isPin ? ' is-pinned' : '') + (isSelected ? ' is-selected' : '')}
      style={{ position: 'relative' }}>

      {/* Native button covers the full card — no non-interactive onClick needed */}
      <button type="button"
        style={{ position: 'absolute', inset: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 'inherit' }}
        onClick={() => onOpen(note)}
        aria-label={`Open note: ${note.noteTitle || 'Untitled'}`} />

      {/* Content sits above clickzone — non-action parts pass clicks through */}
      <div className="nx-note-card-head" style={{ position: 'relative', pointerEvents: 'none' }}>
        <span className="nx-note-card-tone" style={{ background: `color-mix(in srgb, ${tone} 9%, transparent)`, color: tone }}>
          <NexusIcon name="StickyNote" size={12} />
        </span>
        <div className="nx-note-card-meta">
          {note.noteTitle
            ? <span className="nx-note-card-title">{note.noteTitle}</span>
            : <span className="nx-note-card-title" style={{ color: 'var(--nx-text-faint)', fontStyle: 'italic', fontWeight: 400 }}>Untitled</span>
          }
          <div className="nx-note-card-byline">
            <Avatar name={note.who} tone={tone} size={14} />
            <span>{note.who}</span>
            <span className="nx-note-card-dot">·</span>
            <span className="nx-muted">{note.time}</span>
          </div>
        </div>
        <div className="nx-note-card-actions" style={{ pointerEvents: 'auto' }}>
          <button type="button" className={'nx-note-act' + (isPin ? ' is-active' : '')}
            onClick={e => { e.stopPropagation(); onPin(note.id); }} title={isPin ? 'Unpin' : 'Pin'}>
            <NexusIcon name="Pin" size={11} />
          </button>
          <button type="button" className="nx-note-act is-danger"
            onClick={e => { e.stopPropagation(); onDelete(note.id); }} title="Delete note">
            <NexusIcon name="Trash2" size={11} />
          </button>
        </div>
      </div>

      <p className="nx-note-card-body is-clamped" style={{ position: 'relative', pointerEvents: 'none' }}>
        {note.detail || <em style={{ color: 'var(--nx-text-faint)' }}>No content</em>}
      </p>

      <div style={{ position: 'relative', pointerEvents: 'none' }}>
        <NoteLinkedBadge note={note} people={people} companies={companies} deals={deals} />
      </div>
    </div>
  );
};

/* ── NoteTableRow (List view) ──────────────────────────────────── */
const NoteTableRow = ({ note, pinned, isSelected, people, companies, deals, onOpen, onPin, onDelete }) => {
  const tone  = noteTone(note.who);
  const isPin = pinned.has(note.id);
  return (
    <tr
      className={'nx-tr' + (isSelected ? ' is-selected' : '')}
      onClick={() => onOpen(note)}
      onKeyDown={e => { if (e.key === 'Enter') { onOpen(note); } }}
      tabIndex={0}
    >
      <td style={{ width: 36, paddingRight: 0 }}>
        <span className="nx-note-card-tone" style={{ background: `color-mix(in srgb, ${tone} 9%, transparent)`, color: tone, width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 'var(--nx-radius)', flexShrink: 0 }}>
          <NexusIcon name="StickyNote" size={11} />
        </span>
      </td>
      <td>
        <span className={'nx-note-tbl-title' + (!note.noteTitle ? ' nx-note-tbl-untitled' : '')}>
          {note.noteTitle || 'Untitled'}
        </span>
        <span className="nx-note-tbl-preview">{note.detail}</span>
      </td>
      <td style={{ width: 180 }}>
        <NoteLinkedBadge note={note} people={people} companies={companies} deals={deals} size="sm" />
      </td>
      <td style={{ width: 140 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Avatar name={note.who} tone={tone} size={16} />
          <span style={{ fontSize: 12 }}>{note.who}</span>
        </div>
      </td>
      <td style={{ width: 110, fontSize: 12, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>
        {_fmtDate(note.createdAt)}
      </td>
      <td style={{ width: 110, fontSize: 12, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>
        {_fmtDate(note.updatedAt)}
      </td>
      <td className="nx-td-end" onClick={e => e.stopPropagation()}>
        <div className="nx-note-tbl-actions">
          <button type="button" className={'nx-note-act' + (isPin ? ' is-active' : '')} onClick={() => onPin(note.id)} title={isPin ? 'Unpin' : 'Pin'}>
            <NexusIcon name="Pin" size={11} />
          </button>
          <button type="button" className="nx-note-act is-danger" onClick={() => onDelete(note.id)} title="Delete note">
            <NexusIcon name="Trash2" size={11} />
          </button>
        </div>
      </td>
    </tr>
  );
};

/* ══════════════════════════════════════════════════════════════
   NotesView — main module
   ══════════════════════════════════════════════════════════════ */
const NotesView = () => {
  const store     = useStore();
  const toast     = useToast();
  const wsId      = store.workspaceId;
  const myName    = window.__nx_auth?.user?.name || 'You';
  const allNotes  = store.notes    || [];
  const people    = store.people   || [];
  const companies = store.companies || [];
  const deals     = store.deals    || [];

  /* ── Pin persistence ── */
  const [pinned, setPinned] = useStateN(() => _loadPins(wsId));
  useEffectN(() => { setPinned(_loadPins(wsId)); }, [wsId]);
  const togglePin = useCallbackN((id) => setPinned(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    _savePins(wsId, n);
    return n;
  }), [wsId]);

  /* ── View state ── */
  const [tab,          setTab]          = useStateN('all');
  const [view,         setView]         = useStateN('grid');
  const [composerOpen, setComposerOpen] = useStateN(false);
  const [selectedNote, setSelectedNote] = useStateN(null);

  /* ── Filters (session-persistent) ── */
  const fs = useFilters('notes', {});
  const { search, setSearch, filters, setFilter, clearAll, activeCount } = fs;
  const linkFilter = filters.link || null;

  /* ── Table state (sort) ── */
  const ts = useTableState('notes', allNotes);

  /* ── Sort handler — avoids nested ternary ── */
  const handleSortChange = (e) => {
    const val = e.target.value;
    if (!val) {
      if (ts.sort) { ts.onSort(ts.sort.field); }
      return;
    }
    const [field] = val.split('-');
    ts.onSort(field);
  };

  /* ── Derived filtered + sorted notes ── */
  const filtered = useMemoN(() => {
    let r = allNotes;
    if (tab === 'pinned') r = r.filter(n => pinned.has(n.id));
    else if (tab === 'mine') r = r.filter(n => n.who === myName);
    if (linkFilter === 'Person')   r = r.filter(n =>  n.personId);
    if (linkFilter === 'Company')  r = r.filter(n =>  n.companyId);
    if (linkFilter === 'Deal')     r = r.filter(n =>  n.dealId);
    if (linkFilter === 'Unlinked') r = r.filter(n => !n.personId && !n.companyId && !n.dealId);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(n =>
        n.noteTitle?.toLowerCase().includes(q) ||
        n.detail?.toLowerCase().includes(q) ||
        n.who?.toLowerCase().includes(q)
      );
    }
    return ts.applySort(r);
  }, [allNotes, tab, pinned, myName, linkFilter, search, ts.applySort]);

  /* ── CRUD ── */
  const handleCreate = useCallbackN(({ body, title, personId, companyId, dealId }) => {
    store.createNote({ body, title, personId, companyId, dealId });
    setComposerOpen(false);
    toast({ tone: 'success', icon: 'StickyNote', title: 'Note created' });
  }, [store, toast]);

  const handleSave = useCallbackN((id, patch) => {
    store.updateNote(id, patch);
    setSelectedNote(n => n?.id === id ? { ...n, detail: patch.body ?? n.detail, noteTitle: patch.title !== undefined ? patch.title : n.noteTitle } : n);
    toast({ tone: 'success', icon: 'Check', title: 'Note updated' });
  }, [store, toast]);

  const handleDelete = useCallbackN((id) => {
    store.confirm({
      title: 'Delete note?',
      description: 'This cannot be undone.',
      danger: true,
      onConfirm: () => {
        store.removeNote(id);
        if (selectedNote?.id === id) { setSelectedNote(null); }
        toast({ tone: 'success', icon: 'Trash2', title: 'Note deleted' });
      },
    });
  }, [store, toast, selectedNote]);

  const openDetail = useCallbackN((note) => {
    const live = allNotes.find(n => n.id === note.id) || note;
    setSelectedNote(live);
  }, [allNotes]);

  /* Keep detail panel in sync with store updates */
  useEffectN(() => {
    if (!selectedNote) { return; }
    const live = allNotes.find(n => n.id === selectedNote.id);
    if (live) { setSelectedNote(live); }
  }, [allNotes]);

  /* ── Empty state message ── */
  const hasActiveFilters = activeCount > 0;
  const emptyMsg = (() => {
    if (hasActiveFilters) return 'No notes match the current filters.';
    if (tab === 'pinned') return 'No pinned notes yet. Pin a note to see it here.';
    if (tab === 'mine')   return 'You have no notes yet. Create your first note using + New Note above.';
    return 'No notes yet. Create your first note using + New Note above.';
  })();

  /* ══ Render ══════════════════════════════════════════════════ */
  return (
    <div className="nx-view">
      <ModuleHeader
        title="Notes"
        count={allNotes.length}
        actions={
          <>
            <div className="nx-segment">
              <IconBtn icon="LayoutGrid" active={view === 'grid'} onClick={() => { setView('grid'); setComposerOpen(false); }} title="Grid view" />
              <IconBtn icon="List"       active={view === 'list'} onClick={() => { setView('list'); setComposerOpen(false); }} title="List view" />
            </div>
            <Btn variant="primary" icon="Plus" onClick={() => setComposerOpen(o => !o)}>New Note</Btn>
          </>
        }
        tabs={[
          { id: 'all',    label: 'All Notes', count: allNotes.length },
          { id: 'pinned', label: 'Pinned',    count: pinned.size },
          { id: 'mine',   label: 'My Notes',  count: allNotes.filter(n => n.who === myName).length },
        ]}
        activeTab={tab}
        onTab={setTab}
        filters={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            {/* Search */}
            <div className="nx-search-wrap" style={{ flex: '0 1 240px', minWidth: 120 }}>
              <NexusIcon name="Search" size={13} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
              <input
                className="nx-search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search notes…"
                aria-label="Search notes"
              />
              {search && (
                <button type="button" className="nx-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                  <NexusIcon name="X" size={11} />
                </button>
              )}
            </div>

            {/* Linked To */}
            <FilterChip
              icon="Link"
              label="Linked To"
              value={linkFilter}
              options={['Person', 'Company', 'Deal', 'Unlinked']}
              onChange={v => setFilter('link', v)}
              active={!!linkFilter}
            />

            {/* Clear all */}
            {hasActiveFilters && (
              <button type="button" className="nx-chip" onClick={clearAll} style={{ color: 'var(--nx-text-faint)', whiteSpace: 'nowrap' }}>
                <NexusIcon name="X" size={10} /> Clear
              </button>
            )}

            {/* Sort — right-aligned */}
            <div className="nx-note-sort">
              <NexusIcon name="ArrowUpDown" size={12} />
              <select aria-label="Sort notes" onChange={handleSortChange}>
                <option value="">Newest first</option>
                <option value="createdAt-asc">Oldest first</option>
                <option value="noteTitle-asc">Title A–Z</option>
                <option value="noteTitle-desc">Title Z–A</option>
                <option value="updatedAt-desc">Recently updated</option>
              </select>
            </div>
          </div>
        }
      />

      <div className="nx-notes-page">
        {composerOpen && (
          <NoteComposer
            people={people}
            companies={companies}
            deals={deals}
            onSave={handleCreate}
            onClose={() => setComposerOpen(false)}
          />
        )}

        {filtered.length === 0 && (
          <div className="nx-empty" style={{ flexDirection: 'column', gap: 8, padding: '64px 0' }}>
            <NexusIcon name="StickyNote" size={32} style={{ color: 'var(--nx-text-faint)' }} />
            <span style={{ color: 'var(--nx-text-muted)', fontSize: 14 }}>{emptyMsg}</span>
          </div>
        )}
        {filtered.length > 0 && view === 'grid' && (
          <div className="nx-notes-grid">
            {filtered.map(n => (
              <NoteCard
                key={n.id}
                note={n}
                pinned={pinned}
                isSelected={selectedNote?.id === n.id}
                people={people}
                companies={companies}
                deals={deals}
                onOpen={openDetail}
                onPin={togglePin}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
        {filtered.length > 0 && view === 'list' && (
          <div className="nx-notes-table-wrap">
            <table className="nx-table nx-notes-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <SortableTh field="noteTitle" sort={ts.sort} onSort={ts.onSort}>Title</SortableTh>
                  <th style={{ width: 160 }}>Linked To</th>
                  <SortableTh field="who"       sort={ts.sort} onSort={ts.onSort} style={{ width: 140 }}>Author</SortableTh>
                  <SortableTh field="createdAt" sort={ts.sort} onSort={ts.onSort} style={{ width: 120 }}>Created</SortableTh>
                  <SortableTh field="updatedAt" sort={ts.sort} onSort={ts.onSort} style={{ width: 120 }}>Updated</SortableTh>
                  <th scope="col" className="nx-th-end" style={{ width: 72 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(n => (
                  <NoteTableRow
                    key={n.id}
                    note={n}
                    pinned={pinned}
                    isSelected={selectedNote?.id === n.id}
                    people={people}
                    companies={companies}
                    deals={deals}
                    onOpen={openDetail}
                    onPin={togglePin}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedNote && (
        <NoteDetailPanel
          key={selectedNote.id}
          note={selectedNote}
          pinned={pinned}
          people={people}
          companies={companies}
          deals={deals}
          onClose={() => setSelectedNote(null)}
          onPin={togglePin}
          onSave={handleSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
window.NotesView = NotesView;
